// Library methods
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import TableSkeleton from "src/components/containers/shared/TableSkeleton/TableSkeleton";
import CustomPagination from "src/components/shared/pagination/CustomPagination/CustomPagination";
import TableHeader from "src/components/shared/tables/TableHeader/TableHeader";
import RecentProgressionAnalysisCard from "./RecentProgressionAnalysisCard/RecentProgressionAnalysisCard";

// Utils
import {
  getItemsToRender,
  getPageCount,
} from "src/utils/pagination/paginationHelper";
import { multiKeyCompare } from "src/utils/tables/tableHelper";
import { getUtcStartEndDate, getLocalDate } from "src/utils/date/dateHelper";

// Types
import { OrderType } from "src/types/sharedTypes";
import { ProgressionAnalysisType } from "src/services/exams/types";

// Hooks
import { useRecentlyCompletedProgressionAnalysesData } from "src/services/exams/hooks/dashboard-exams/useRecentlyCompletedProgressionAnalysesData";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Styles
import { useTheme } from "@mui/material/styles";

const LOADING_NUM_OF_ROWS = 5;
const MAX_NUM_OF_ROWS = 10;

// The clinic-wide dashboard endpoint carries patient info the way
// getRecentlyCompletedExams' ExamType does, which the patient-scoped
// ProgressionAnalysisType (used from within a single patient's page) does
// not need to.
type ExtendedProgressionAnalysisType = ProgressionAnalysisType & {
  patient: { id: string; firstName: string; lastName: string };
  firstName: string;
  lastName: string;
  formattedCreationDate: string;
};

type RecentProgressionAnalysesType = {
  timeRange: number;
  searchTerm: string;
  onLoadingChange?: (isLoading: boolean) => void;
  forceExpandKey?: number;
};

const RecentProgressionAnalyses = ({
  timeRange,
  searchTerm,
  onLoadingChange,
  forceExpandKey,
}: RecentProgressionAnalysesType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const theme = useTheme();

  const recentFilterPayload = useMemo(
    () => ({
      recentFilterDate: getUtcStartEndDate(timeRange, clinicInfo?.timezone)
        .startOfUTC,
    }),
    [timeRange, clinicInfo?.timezone]
  );

  const {
    data: recentProgressionAnalysesData,
    isLoading: isLoadingRecentProgressionAnalyses,
    isError: isErrorRecentProgressionAnalyses,
  } = useRecentlyCompletedProgressionAnalysesData(
    clinicInfo?.id,
    recentFilterPayload
  );

  const formattedProgressionAnalyses: Array<ExtendedProgressionAnalysisType> =
    useMemo(
      () =>
        (
          (recentProgressionAnalysesData ??
            []) as Array<ExtendedProgressionAnalysisType>
        )?.map((progressionAnalysis) => ({
          ...progressionAnalysis,
          firstName: progressionAnalysis.patient?.firstName,
          lastName: progressionAnalysis.patient?.lastName,
          formattedCreationDate: getLocalDate(
            progressionAnalysis.creationDate,
            clinicInfo?.timezone
          ),
        })),
      [recentProgressionAnalysesData, clinicInfo?.timezone]
    );

  const hasRecentProgressionAnalyses = useMemo(
    () =>
      !isLoadingRecentProgressionAnalyses &&
      !isErrorRecentProgressionAnalyses &&
      formattedProgressionAnalyses &&
      formattedProgressionAnalyses?.length > 0,
    [
      isLoadingRecentProgressionAnalyses,
      isErrorRecentProgressionAnalyses,
      formattedProgressionAnalyses,
    ]
  );

  const hasNoRecentProgressionAnalyses = useMemo(
    () =>
      !isLoadingRecentProgressionAnalyses &&
      (isErrorRecentProgressionAnalyses ||
        !formattedProgressionAnalyses?.length),
    [
      isLoadingRecentProgressionAnalyses,
      isErrorRecentProgressionAnalyses,
      formattedProgressionAnalyses,
    ]
  );

  const orderByCreationDate: Array<keyof ExtendedProgressionAnalysisType> = [
    "creationDate",
  ];
  const orderByPatientName: Array<keyof ExtendedProgressionAnalysisType> = [
    "firstName",
    "lastName",
  ];
  const orderByEye: Array<keyof ExtendedProgressionAnalysisType> = ["eye"];

  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(true);

  const [order, setOrder] = useState<OrderType>("desc");

  const [orderByOptions, setOrderByOptions] = useState<
    Array<keyof ExtendedProgressionAnalysisType>
  >([...orderByCreationDate, ...orderByPatientName]);

  const headCells = [
    {
      id: "patient",
      label: t("Patient"),
      gridSize: 3.35,
      orderUsing: [
        ...orderByPatientName,
        ...orderByCreationDate,
        ...orderByEye,
      ],
    },
    {
      id: "eye",
      label: t("Eye"),
      gridSize: 4.15,
      orderUsing: [
        ...orderByEye,
        ...orderByPatientName,
        ...orderByCreationDate,
      ],
    },
    {
      id: "creation-date",
      label: t("Created"),
      gridSize: 4.5,
      orderUsing: [
        ...orderByCreationDate,
        ...orderByPatientName,
        ...orderByEye,
      ],
    },
  ];

  const handleRequestSort = useCallback(
    (
      _: React.MouseEvent<HTMLButtonElement>,
      property: Array<keyof ExtendedProgressionAnalysisType>
    ) => {
      const isAsc =
        orderByOptions.length &&
        orderByOptions[0] === property[0] &&
        order === "asc";

      setOrder(isAsc ? "desc" : "asc");
      setOrderByOptions(property);
    },
    [order, orderByOptions]
  );

  // Filtered rows
  const filteredRows = useMemo(() => {
    let filterArray = formattedProgressionAnalyses?.filter(
      (progressionAnalysis) => {
        const debouncedLowerCase = searchTerm?.toLowerCase();

        const searchTermHasName =
          `${progressionAnalysis?.firstName} ${progressionAnalysis?.lastName}`
            ?.toLowerCase()
            ?.includes(debouncedLowerCase);

        const searchTermHasEye = progressionAnalysis?.eye
          ?.toString()
          ?.toLowerCase()
          ?.includes(debouncedLowerCase);

        const searchTermHasCreationDate =
          progressionAnalysis?.formattedCreationDate
            ?.toString()
            ?.toLowerCase()
            ?.includes(debouncedLowerCase);

        return (
          searchTermHasName || searchTermHasEye || searchTermHasCreationDate
        );
      }
    );

    return multiKeyCompare<ExtendedProgressionAnalysisType>(
      filterArray,
      order === "asc",
      orderByOptions
    );
  }, [formattedProgressionAnalyses, order, orderByOptions, searchTerm]);

  const hasNoSearchResults = useMemo(
    () => searchTerm?.length > 0 && !filteredRows?.length,
    [searchTerm, filteredRows]
  );

  // progression analyses to render
  const progressionAnalysesOnCurrentPage = useMemo(() => {
    return getItemsToRender(filteredRows, page, MAX_NUM_OF_ROWS);
  }, [filteredRows, page]);

  // Pagination change
  const onPaginationChange = useCallback(
    (_: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
    },
    [setPage]
  );

  useEffect(() => setPage(1), [filteredRows]);

  useEffect(() => {
    onLoadingChange?.(isLoadingRecentProgressionAnalyses);
  }, [isLoadingRecentProgressionAnalyses, onLoadingChange]);

  useEffect(() => {
    if (forceExpandKey) setExpanded(true);
  }, [forceExpandKey]);

  return (
    <Box
      display="flex"
      width="100%"
      height={expanded ? "100%" : "auto"}
      flexDirection="column"
      sx={{
        backgroundColor:
          theme.palette.mode === "dark" ? "#5B6D7A40" : "#5B6D7A20",
        borderRadius: "4px",
        p: "8px",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="12px"
      >
        <Typography
          alignContent="center"
          fontWeight={600}
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
          maxWidth="100%"
        >
          {t("Recent Analyses")}
        </Typography>

        <Button
          onClick={() => setExpanded((prev) => !prev)}
          size="small"
          sx={{
            color: "inherit",
            textTransform: "none",
            textDecoration: "underline",
          }}
        >
          {expanded ? t("Collapse") : t("Expand")}
        </Button>
      </Box>

      <Collapse in={expanded} timeout="auto">
        {isLoadingRecentProgressionAnalyses && (
          <TableSkeleton numOfRows={LOADING_NUM_OF_ROWS} />
        )}

        {hasRecentProgressionAnalyses && (
          <Box display="flex" flexDirection="column" mt="8px" gap="12px">
            <TableHeader<ExtendedProgressionAnalysisType>
              headCells={headCells}
              order={order}
              orderBy={orderByOptions?.[0] ?? "creationDate"}
              onRequestSort={handleRequestSort}
              rowCount={progressionAnalysesOnCurrentPage.length}
              numSelected={0}
            />
            {progressionAnalysesOnCurrentPage?.map((progressionAnalysis) => (
              <RecentProgressionAnalysisCard
                key={progressionAnalysis.id}
                progressionAnalysis={progressionAnalysis}
              />
            ))}
            <Box mt="12px">
              <CustomPagination
                page={page}
                count={getPageCount(filteredRows?.length, MAX_NUM_OF_ROWS)}
                onChange={onPaginationChange}
              />
            </Box>
          </Box>
        )}

        {/* Error messages */}
        {!hasNoRecentProgressionAnalyses && hasNoSearchResults && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            my="60px"
          >
            <Typography textAlign="center" fontWeight={600}>
              {t("No Recently Completed Progression Analyses Found.")}
            </Typography>
            <Typography>
              {t("Change your search term to see more results.")}
            </Typography>
          </Box>
        )}
        {hasNoRecentProgressionAnalyses && (
          <Box display="flex" justifyContent="center" my="60px">
            <Typography textAlign="center">
              {t("No Recently Completed Progression Analyses Found.")}
            </Typography>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default RecentProgressionAnalyses;
